/**
 * Quick add from product cards: one tap on a size adds that variant and opens the cart drawer.
 * Falls back to the cart page when the drawer is not in use. Also swaps the card image when a
 * colour dot is hovered (desktop pointers only).
 *
 * Markup contract (snippets/card-product.liquid): <vm-quick-add> inside .vm-card, buttons with data-variant-id,
 * and a p.vm-quick-add__status.visually-hidden live region. A failed add shows its message in that region
 * (visible, styles in vm-theme.css "Quick add: error message") for a few seconds.
 */
if (!customElements.get('vm-quick-add')) {
  class VmQuickAdd extends HTMLElement {
    static ERROR_MS = 4000;

    connectedCallback() {
      this.status = this.querySelector('.vm-quick-add__status');
      if (this.listening) return;
      this.listening = true;
      this.addEventListener('click', this.#onClick.bind(this));
    }

    disconnectedCallback() {
      this.#clearError();
    }

    async #onClick(event) {
      const button = event.target.closest('[data-variant-id]');
      if (!button || button.disabled || this.busy) return;
      event.preventDefault();
      this.busy = true;
      this.#clearError();
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');

      const cart = document.querySelector('cart-drawer');
      const body = new FormData();
      body.append('id', button.dataset.variantId);
      body.append('quantity', '1');
      if (cart) {
        body.append('sections', cart.getSectionsToRender().map((section) => section.id).join(','));
        body.append('sections_url', window.location.pathname);
      }

      try {
        const response = await fetch(`${window.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
          body,
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          this.#showError(button, data.description || data.message || window.cartStrings?.error || 'Error');
          return;
        }
        this.#announce(window.vmStrings?.added || 'Added to your bag');
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'vm-quick-add', productVariantId: button.dataset.variantId, cartData: data });
        }
        if (cart) {
          // Focus returns here when the drawer closes. The size buttons are tabindex="-1" and the bar hides
          // without hover, so use the card's title link (tabbable, always visible).
          cart.setActiveElement(this.closest('.vm-card')?.querySelector('.vm-card__link') || button);
          cart.renderContents(data);
        } else {
          window.location.href = window.routes.cart_url;
        }
      } catch (error) {
        console.error(error);
        this.#showError(button, window.cartStrings?.error || 'Error');
      } finally {
        this.busy = false;
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      }
    }

    #announce(message, visible = false) {
      if (!this.status) return;
      this.status.textContent = '';
      requestAnimationFrame(() => {
        this.status.classList.toggle('visually-hidden', !visible);
        this.status.classList.toggle('is-visible', visible);
        this.status.textContent = message;
      });
    }

    // Visible text, not only the red border: the click must not look like it did nothing.
    #showError(button, message) {
      button.classList.add('is-error');
      this.classList.add('has-error');
      this.#announce(message, true);
      clearTimeout(this.errorTimer);
      this.errorTimer = setTimeout(() => this.#clearError(), VmQuickAdd.ERROR_MS);
    }

    #clearError() {
      clearTimeout(this.errorTimer);
      if (!this.classList.contains('has-error')) return;
      this.classList.remove('has-error');
      this.querySelectorAll('.is-error').forEach((element) => element.classList.remove('is-error'));
      if (this.status) {
        this.status.classList.remove('is-visible');
        this.status.classList.add('visually-hidden');
        this.status.textContent = '';
      }
    }
  }
  customElements.define('vm-quick-add', VmQuickAdd);
}

// Colour dot hover: preview the sibling colour's photo on the card (desktop only)
(() => {
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  document.addEventListener(
    'pointerover',
    (event) => {
      const dot = event.target.closest?.('.vm-dot[data-swap-src]');
      if (!dot) return;
      const card = dot.closest('.vm-card');
      const img = card?.querySelector('.vm-card__img--primary');
      if (!img) return;
      if (!img.dataset.originalSrc) {
        img.dataset.originalSrc = img.currentSrc || img.src;
        img.dataset.originalSrcset = img.getAttribute('srcset') || '';
      }
      img.removeAttribute('srcset');
      img.src = dot.dataset.swapSrc;
      card.classList.add('is-swapped');
      dot.addEventListener(
        'pointerleave',
        () => {
          if (img.dataset.originalSrcset) img.setAttribute('srcset', img.dataset.originalSrcset);
          img.src = img.dataset.originalSrc;
          card.classList.remove('is-swapped');
        },
        { once: true },
      );
    },
    { passive: true },
  );
})();
